import http from '@/api/http'
import { TReportJobStatus } from '@/types/report'

import {
  TGetPredictionsPreviewResponse,
  TGetPredictionParams,
  TGetPredictionsResponse,
  TGetCheckPredictionsResponse,
} from './types'

export const getPredictionsPreview = async (filters: TGetPredictionParams) => {
  const {
    coSphere,
    coRegion,
    tpLocation,
    coEducationStageDisplay,
    nuVersao,
    ...rest
  } = filters as unknown as Record<string, unknown>

  const params: Record<string, unknown> = Object.fromEntries(
    Object.entries(rest).filter(
      ([, v]) => v !== null && v !== undefined && v !== false && v !== ''
    )
  )

  if (nuVersao !== undefined && nuVersao !== '' && nuVersao !== null) {
    params.nu_versao = nuVersao
  }

  if (coSphere) {
    params.co_sphere = Array.isArray(coSphere)
      ? (coSphere as Array<unknown>).join(',')
      : String(coSphere)
  }

  if (coRegion) {
    params.co_region = Array.isArray(coRegion)
      ? (coRegion as Array<unknown>).join(',')
      : String(coRegion)
  }

  if (tpLocation !== undefined) {
    params.tp_location =
      tpLocation === null || tpLocation === undefined
        ? tpLocation
        : String(tpLocation)
  }

  const response = await http.get<TGetPredictionsPreviewResponse>(
    '/predictions/preview/',
    {
      params,
    }
  )

  return response.data
}

export const getPredictions = async (filters: TGetPredictionParams) => {
  const {
    coSphere: coSphere2,
    coRegion: coRegion2,
    tpLocation: tpLocation2,
    coEducationStageDisplay,
    nuVersao: nuVersao2,
    ...rest2
  } = filters as unknown as Record<string, unknown>
  const params2: Record<string, unknown> = { ...rest2 }

  if (nuVersao2 !== undefined && nuVersao2 !== '' && nuVersao2 !== null) {
    params2.nu_versao = nuVersao2
  }

  if (coSphere2) {
    params2.co_sphere = Array.isArray(coSphere2)
      ? (coSphere2 as Array<unknown>).join(',')
      : String(coSphere2)
  }

  if (coRegion2) {
    params2.co_region = Array.isArray(coRegion2)
      ? (coRegion2 as Array<unknown>).join(',')
      : String(coRegion2)
  }

  if (tpLocation2 !== undefined) {
    params2.tp_location =
      tpLocation2 === null || tpLocation2 === undefined
        ? tpLocation2
        : String(tpLocation2)
  }

  const response = await http.get<TGetPredictionsResponse>('/predictions/', {
    params: params2,
  })

  return response.data
}

export const getCheckPredictions = async () => {
  const response = await http.get<TGetCheckPredictionsResponse>(
    '/prediction-processing/status/'
  )

  return response.data
}

export type { TReportJobStatus }

export const requestPredictionReport = async (
  filters: TGetPredictionParams
): Promise<{ jobId: string; status: string; message: string }> => {
  const f = filters as unknown as Record<string, unknown>

  const params: Record<string, string> = {}

  if (f.nuCensusYear) params.nu_census_year = String(f.nuCensusYear)
  if (f.nuYearPrediction) params.nu_year_prediction = String(f.nuYearPrediction)
  if (f.nuVersao !== undefined && f.nuVersao !== '' && f.nuVersao !== null)
    params.nu_versao = String(f.nuVersao)
  if (
    f.isAllStages !== undefined &&
    f.isAllStages !== false &&
    f.isAllStages !== ''
  )
    params.is_all_stages = String(f.isAllStages)
  if (f.stPnldService) params.st_pnld_service = String(f.stPnldService)
  if (f.coEducationStage) params.co_education_stage = String(f.coEducationStage)
  if (f.coCity) params.co_city = String(f.coCity)
  if (f.coUf) params.co_uf = String(f.coUf)
  if (f.coEntity) params.co_entity = String(f.coEntity)
  if (f.tpDependency) params.tp_dependency = String(f.tpDependency)
  if (
    f.tpLocation !== undefined &&
    f.tpLocation !== null &&
    f.tpLocation !== ''
  )
    params.tp_location = String(f.tpLocation)
  if (f.coSphere) {
    params.co_sphere = Array.isArray(f.coSphere)
      ? (f.coSphere as Array<unknown>).join(',')
      : String(f.coSphere)
  }
  if (f.coRegion) {
    params.co_region = Array.isArray(f.coRegion)
      ? (f.coRegion as Array<unknown>).join(',')
      : String(f.coRegion)
  }
  if (f.typePrediction) params.type_prediction = String(f.typePrediction)

  const queryString = new URLSearchParams(params).toString()

  const response = await http.post<{
    job_id: string
    status: string
    message: string
  }>(`/predictions/request-report/?${queryString}`)

  const data = response.data as unknown as Record<string, string>

  return {
    jobId: data.job_id ?? data.jobId,
    status: response.data.status,
    message: response.data.message,
  }
}

export const getPredictionReportStatus = async (
  jobId: string
): Promise<TReportJobStatus> => {
  const response = await http.get<TReportJobStatus>(
    `/predictions/report-status/${jobId}/`
  )
  return response.data
}

export const cancelAllPredictions = async (): Promise<{
  cancelled: number
}> => {
  const response = await http.post<{
    cancelled: number
  }>('/prediction-processing/cancel-all/')

  return response.data
}
